import type { SortKey, SortState } from './findingSort';

/** Session-scoped persistence for the findings filter state, keyed per run.
 *
 *  The filter state lives in `useFindingsReview`, which is mounted inside
 *  `FindingsTabs` on the `/runs/$id` route. Navigating to a finding detail
 *  page (`/runs/$id/findings/$findingId`, a sibling route) unmounts it, so
 *  the filters would reset on return. Round-tripping them through
 *  sessionStorage keeps "the filters that were there before" when the
 *  practitioner comes back — and across a reload — without bloating the URL
 *  with 13 params.
 *
 *  Values are plain primitives (the hook casts the enum-typed ones on read)
 *  to keep this module free of a circular import back into the hook. */
export interface StoredFilters {
  ruleFilter?: string;
  provinceFilter?: string;
  supplierFilter?: string;
  transactionTypeFilter?: string;
  classFilter?: string;
  glAccountFilter?: string;
  currencyFilter?: string;
  outcomeFilter?: string;
  searchQuery?: string;
  statusFilter?: string;
  approvalReasonFilter?: string;
  reasonCodesFilter?: string[];
  amountMin?: string;
  amountMax?: string;
  effRateMin?: string;
  effRateMax?: string;
  carriedOnlyFilter?: boolean;
  invoiceFilter?: string;
  sourceFilter?: string;
  ledgerSourceFilter?: string;
}

export function findingFilterStorageKey(runId: string): string {
  return `findings-filters:${runId}`;
}

/** Read the stored filters for a run. Returns `{}` on a miss, corrupt JSON,
 *  or when sessionStorage is unavailable (private mode / SSR). */
export function readStoredFilters(key: string): StoredFilters {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    return typeof parsed === 'object' && parsed !== null ? (parsed as StoredFilters) : {};
  } catch {
    return {};
  }
}

/** Persist the filters for a run. Silently no-ops if sessionStorage throws. */
export function writeStoredFilters(key: string, filters: StoredFilters): void {
  try {
    sessionStorage.setItem(key, JSON.stringify(filters));
  } catch {
    // sessionStorage can throw (private mode, quota) — persistence is a
    // nicety, not a correctness requirement, so swallow it.
  }
}

// The list-view column sort and the last-visited row live under their own
// per-run keys, NOT in the filter blob above: `useFindingsReview`
// full-replaces the filter blob on every filter change, which would clobber
// anything else stored alongside it.

export function findingSortStorageKey(runId: string): string {
  return `findings-sort:${runId}`;
}

const SORT_DIRS = new Set(['asc', 'desc']);
const SORT_KEYS = new Set<SortKey>(['amount', 'date', 'status', 'supplier']);

/** Read the stored list-view sort. Returns `null` on a miss or a value that
 *  isn't a well-formed `{ key, dir }` with a supported key + dir (corrupt,
 *  older shape, or a since-removed sort column). */
export function readStoredSort(key: string): SortState | null {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      SORT_KEYS.has((parsed as SortState).key) &&
      SORT_DIRS.has((parsed as SortState).dir)
    ) {
      return parsed as SortState;
    }
    return null;
  } catch {
    return null;
  }
}

/** Persist (or clear, when `null`) the list-view sort. */
export function writeStoredSort(key: string, sort: SortState | null): void {
  try {
    if (sort === null) sessionStorage.removeItem(key);
    else sessionStorage.setItem(key, JSON.stringify(sort));
  } catch {
    // persistence is a nicety — swallow private-mode / quota errors.
  }
}

export function lastVisitedFindingKey(runId: string): string {
  return `findings-last-visited:${runId}`;
}

/** Per-run key for the last-opened supplier row (suppliers tab), keyed by
 *  supplier name. Parallel to `lastVisitedFindingKey`. */
export function lastVisitedSupplierKey(runId: string): string {
  return `suppliers-last-visited:${runId}`;
}

/** The row id (finding id, supplier name, …) last clicked to open a detail
 *  page, for highlighting + scrolling to the row on return. `null` on a
 *  miss. Generic over the table — the caller supplies the storage key. */
export function readLastVisitedRow(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

export function writeLastVisitedRow(key: string, rowId: string): void {
  try {
    sessionStorage.setItem(key, rowId);
  } catch {
    // persistence is a nicety — swallow private-mode / quota errors.
  }
}

/** Per-run key for the suppliers tab's chosen sort. */
export function supplierSortStorageKey(runId: string): string {
  return `suppliers-sort:${runId}`;
}

/** Read/write the suppliers tab's sort key as a plain string; the caller
 *  validates it against its own union before use. `null` on a miss. */
export function readStoredSupplierSort(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

export function writeStoredSupplierSort(key: string, value: string): void {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    // persistence is a nicety — swallow private-mode / quota errors.
  }
}

export function visibleFindingOrderKey(runId: string): string {
  return `findings-visible-order:${runId}`;
}

/** Read the list view's current (filtered + sorted) finding-id order, so the
 *  detail page can step Prev/Next through it. Returns `[]` on a miss or a
 *  value that isn't an array of strings. */
export function readVisibleFindingOrder(key: string): string[] {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (Array.isArray(parsed) && parsed.every((id) => typeof id === 'string')) {
      return parsed as string[];
    }
    return [];
  } catch {
    return [];
  }
}

export function writeVisibleFindingOrder(key: string, order: string[]): void {
  try {
    sessionStorage.setItem(key, JSON.stringify(order));
  } catch {
    // persistence is a nicety — swallow private-mode / quota errors.
  }
}
